<template>
  <header>
    <div
      class="fixed-banner"
      :style="{
        'background-image': `url(${require('@/assets/images/singleHome/single_home_banner.png')})`,
      }"
    >
      <div class="container">
        <div class="d-flex justify-content-center flex-column ptb--50">
          <h2 class="color-white">{{ $t("single_home.banner.title") }}</h2>
          <h4 class="color-white">{{ $t("single_home.banner.desc") }}</h4>
        </div>
      </div>
    </div>
    <div class="shadow bg-white top-0 w-100 navbar">
      <div class="container">
        <scrollactive
          ref="scrollactive"
          activeClass="active"
          class="flex-1"
          :duration="100"
          :offset="50"
          @itemchanged="onActiveItemChanged"
        >
          <ul class="d-flex m-0 p-0">
            <li v-for="(tab, index) in tabs" :key="index" ref="tabItems">
              <span
                :data-section-selector="tab.target"
                class="scrollactive-item nav-item"
                >{{ tab.name }}</span
              >
            </li>
          </ul>
        </scrollactive>
        <switch-language />
      </div>
    </div>
  </header>
</template>
<script>
import Scrollactive from "vue-scrollactive/src/scrollactive.vue";
import SwitchLanguage from "@/components/common/header/SwithLanguage.vue";

export default {
  name: "SingleHeader",
  components: { Scrollactive, SwitchLanguage },
  data() {
    return {
      spacer: null,
      activeTabIndex: 0,
      rafId: null,
      resizeTimer: null, // 添加缺失的resizeTimer
    };
  },
  computed: {
    tabs() {
      return [
        {
          name: this.$t("single_home.crm_service.tab_name"),
          target: ".dynamics-365",
        },
        {
          name: this.$t("single_home.erp_service.tab_name"),
          target: ".dynamics-erp",
        },
        {
          name: this.$t("single_home.pp_service.tab_name"),
          target: ".power-platform",
        },
        {
          name: this.$t("single_home.ip_service.title"),
          target: ".ip-product",
        },
        { name: this.$t("single_home.case.title"), target: ".cases" },
        { name: this.$t("single_home.about_us.title"), target: ".about-us" },
      ];
    },
    isMobile() {
      return window.innerWidth <= 767;
    },
  },
  mounted() {
    this.setupEventListeners();
    this.$nextTick(() => {
      this.checkInitialActiveItem();
    });
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    /**
     * 设置事件监听器
     */
    setupEventListeners() {
      window.addEventListener("scroll", this.handleScroll);
      window.addEventListener("scroll", this.onPageScroll, { passive: true });
      window.addEventListener("resize", this.handleResize);
    },

    /**
     * 清理资源
     */
    cleanup() {
      window.removeEventListener("scroll", this.handleScroll);
      window.removeEventListener("scroll", this.onPageScroll);
      window.removeEventListener("resize", this.handleResize);

      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }

      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }

      this.removeSpacer();
    },

    /**
     * 处理页面滚动以固定导航栏
     */
    handleScroll() {
      const banner = document.querySelector(".fixed-banner");
      const navbar = document.querySelector(".navbar");

      if (!banner || !navbar) return;

      const shouldFix = banner.getBoundingClientRect().bottom <= 0;
      const isFixed = navbar.classList.contains("position-fixed");

      if (shouldFix && !isFixed) {
        this.fixNavbar(navbar);
      } else if (!shouldFix && isFixed) {
        this.unfixNavbar(navbar);
      }
    },

    /**
     * 固定导航栏
     */
    fixNavbar(navbar) {
      navbar.classList.add("position-fixed");
      this.spacer = document.createElement("div");
      this.spacer.className = "navbar-spacer";
      this.spacer.style.height = `${navbar.getBoundingClientRect().height}px`;
      navbar.parentNode.insertBefore(this.spacer, navbar);
    },

    /**
     * 取消固定导航栏
     */
    unfixNavbar(navbar) {
      navbar.classList.remove("position-fixed");
      this.removeSpacer();
    },

    /**
     * 移除占位元素
     */
    removeSpacer() {
      if (this.spacer?.parentNode) {
        this.spacer.parentNode.removeChild(this.spacer);
      }
    },

    /**
     * 页面滚动事件处理
     */
    onPageScroll() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }

      this.rafId = requestAnimationFrame(() => {
        this.checkActiveItemWhileScrolling();
      });
    },

    /**
     * 滚动过程中检查激活项
     */
    checkActiveItemWhileScrolling() {
      if (!this.isMobile) return;

      const activeElement = document.querySelector(".scrollactive-item.active");
      if (this.isValidElement(activeElement)) {
        const parentLi = activeElement.closest("li");
        if (parentLi) {
          const index = Array.from(parentLi.parentNode.children).indexOf(
            parentLi
          );
          // 始终确保激活项居中，即使索引未改变
          this.activeTabIndex = index;
          this.scrollToActiveTab(index, false);
        }
      }
    },

    /**
     * 处理窗口大小改变
     */
    handleResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.scrollToActiveTab(this.activeTabIndex);
      }, 150);
    },

    /**
     * 检查初始激活项
     */
    checkInitialActiveItem() {
      const activeElement = document.querySelector(".scrollactive-item.active");
      if (this.isValidElement(activeElement)) {
        const parentLi = activeElement.closest("li");
        if (parentLi) {
          const index = Array.from(parentLi.parentNode.children).indexOf(
            parentLi
          );
          this.activeTabIndex = index;
          this.scrollToActiveTab(index);
        }
      }
    },

    /**
     * 处理激活项改变事件
     */
    onActiveItemChanged(activeItem) {
      const activeElement = this.getActiveElement(activeItem);
      if (!this.isValidElement(activeElement)) return;

      const parentLi = activeElement.closest("li");
      if (parentLi) {
        const index = Array.from(parentLi.parentNode.children).indexOf(
          parentLi
        );
        this.activeTabIndex = index;
        this.$nextTick(() => {
          this.scrollToActiveTab(index);
        });
      }
    },

    /**
     * 获取激活元素
     */
    getActiveElement(activeItem) {
      if (!activeItem) {
        return document.querySelector(".scrollactive-item.active");
      }

      // 尝试多种方式获取激活元素
      let element = null;
      if (activeItem.element) {
        element = activeItem.element;
      } else if (activeItem.target) {
        element = activeItem.target;
      } else {
        element = activeItem;
      }

      // 验证元素是否有效
      if (this.isValidElement(element)) {
        return element;
      }

      // fallback到手动查询
      return document.querySelector(".scrollactive-item.active");
    },

    /**
     * 验证元素是否有效
     */
    isValidElement(element) {
      return (
        element &&
        typeof element === "object" &&
        typeof element.closest === "function"
      );
    },

    /**
     * 滚动到激活的tab并使其居中
     */
    scrollToActiveTab(index, useSmooth = true) {
      if (!this.isMobile) return;

      this.$nextTick(() => {
        const tabItems = this.$refs.tabItems;
        if (!tabItems || !tabItems[index]) return;

        const container = this.$refs.scrollactive.$el;
        const activeTab = tabItems[index];

        if (container.scrollWidth <= container.offsetWidth) return;

        const scrollPosition = this.calculateScrollPosition(
          container,
          activeTab
        );
        const finalScrollPosition = this.clampScrollPosition(
          container,
          scrollPosition
        );

        requestAnimationFrame(() => {
          container.scrollTo({
            left: finalScrollPosition,
            behavior: useSmooth ? "smooth" : "auto",
          });
        });
      });
    },

    /**
     * 计算滚动位置
     */
    calculateScrollPosition(container, activeTab) {
      const containerWidth = container.offsetWidth;
      const activeTabOffsetLeft = activeTab.offsetLeft;
      const activeTabWidth = activeTab.offsetWidth;
      return activeTabOffsetLeft - containerWidth / 2 + activeTabWidth / 2;
    },

    /**
     * 限制滚动位置在有效范围内
     */
    clampScrollPosition(container, scrollPosition) {
      const maxScroll = container.scrollWidth - container.offsetWidth;
      return Math.max(0, Math.min(scrollPosition, maxScroll));
    },
  },
};
</script>
<style lang="scss" scoped>
header {
  $sm-layout: "only screen and (max-width: 767px)";

  .fixed-banner {
    background-size: 100%;
    max-height: 300px;
    background-repeat: no-repeat;
    background-attachment: fixed;
    background-position: 0 0;

    @media #{$sm-layout} {
      background-size: cover;
      background-position: center;
    }
  }

  .navbar {
    .flex-1 {
      flex: 1;

      @media #{$sm-layout} {
        overflow-x: auto;
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
        }

        will-change: scroll-position;
        -webkit-overflow-scrolling: touch;
        scroll-behavior: smooth;
      }
    }

    z-index: 999;

    ul {
      justify-content: space-around;

      @media #{$sm-layout} {
        text-wrap-mode: nowrap;
      }

      li {
        color: #000000;
        font-weight: bold;
        font-size: 1.5rem;
        position: relative;
        margin: 0;
        padding: 0.8rem;
        cursor: pointer;

        span.active,
        span:hover {
          color: #1457df;

          &:after {
            content: "";
            display: block;
            width: 4rem;
            height: 2px;
            border-radius: 5px;
            background: #1457df;
            position: absolute;
            bottom: 0px;
            left: 50%;
            transform: translateX(-50%);
          }
        }
      }
    }
  }
}
</style>